// @ts-check

import { shape, string, arrayOf, node } from 'prop-types';
import React, { useState, useEffect, useMemo } from 'react';
import { ref as refPropType } from 'common/propTypes';
import { findFirstElementInViewport } from 'menus/providers/MenuScrollListenerProvider/scrolling-utils';
import { MenuScrollListenerContext } from './MenuScrollListenerContext';

/**
 * @typedef {object} MenuSubCategory
 * @property {string} id
 * @property {React.RefObject<Element>} ref
 */

/**
 * @typedef {object} MenuCategory
 * @property {string} id
 * @property {React.RefObject<Element>} ref
 * @property {MenuSubCategory[]} subCategories
 */

/**
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @param {MenuCategory[]} props.menuCategories
 * @returns {React.ReactNode}
 */
export const MenuScrollListenerProvider = ({ children, menuCategories }) => {
  const startingCategory = menuCategories[0];
  const startingSubCategory = menuCategories[0].subCategories[0] || null;

  const [selectedCategory, setSelectedCategory] = useState(startingCategory);
  const [selectedSubcategory, setSelectedSubcategory] = useState(startingSubCategory);

  useEffect(() => {
    const scrollListener = () => {
      const categoryAtTopOfViewport = menuCategories.find(findFirstElementInViewport);
      const subCategoryAtTopOfViewport = categoryAtTopOfViewport.subCategories.find(
        findFirstElementInViewport
      );

      setSelectedCategory(categoryAtTopOfViewport);
      setSelectedSubcategory(subCategoryAtTopOfViewport || null);
    };

    window.addEventListener('scroll', scrollListener);
    return () => {
      window.removeEventListener('scroll', scrollListener);
    };
  }, [menuCategories]);

  const contextValue = useMemo(
    () => ({
      selectedCategory,
      selectedSubcategory,
    }),
    [selectedCategory, selectedSubcategory]
  );

  return (
    <MenuScrollListenerContext.Provider value={contextValue}>
      {children}
    </MenuScrollListenerContext.Provider>
  );
};

MenuScrollListenerProvider.propTypes = {
  children: node,
  menuCategories: arrayOf(
    shape({
      category: string.isRequired,
      id: string.isRequired,
      ref: refPropType.isRequired,
    })
  ).isRequired,
};

MenuScrollListenerProvider.defaultProps = {
  children: null,
};
